import { useSelector, useDispatch } from "react-redux";
import {
  TacoAssetType,
  Project,
  DEFAULT_PROJECT_MAX_PAGE_SIZE,
} from "@amzn/afxtaco-types";
import { reducerType } from "../../redux/reducers";
import { AppDispatch } from "../../redux/store";
import {
  setLoading,
  setError,
  setProjects,
  appendProjects,
  setNextToken,
  setProject,
  addProject,
  removeProject,
  clearProjects,
  setProjectAsset,
  clearProjectAsset,
  clearAllProjectAssets,
} from "../../redux/reducers/Projects";
import {
  getProjects as getProjectsAPI,
  getProject as getProjectAPI,
  createProject as createProjectAPI,
  updateProject as updateProjectAPI,
  deleteProject as deleteProjectAPI,
  CreateProjectRequest,
  UpdateProjectRequest,
} from "../api/projects/projects";
import {
  getAssetDownloadUrl,
  AssetDownloadUrlParams,
} from "../api/assets/assets";

/**
 * Asset types whose S3 objects are text (markdown, XML or JSON) rather than
 * binary. Anything absent from here is fetched as a blob only, so `content` stays
 * undefined and any caller parsing the text silently gets nothing.
 */
export const TEXT_ASSET_TYPES: ReadonlySet<TacoAssetType> = new Set([
  TacoAssetType.ARCHITECTURE_SUMMARY,
  TacoAssetType.IMPLEMENTATION_DETAILS,
  TacoAssetType.THINKING,
  TacoAssetType.BIDI_DIAGRAM,
  TacoAssetType.BLOG_POST,
  TacoAssetType.DELIVERY_DEFINITION,
]);

/**
 * Custom hook for managing projects state and API calls
 *
 * Provides access to projects from Redux store and methods to interact with the Projects API
 * while keeping the Redux store in sync.
 *
 * Projects are automatically loaded when the app initializes (via DataProvider).
 * Individual pages don't need to call fetchProjects() unless they want to force a refresh.
 *
 * @example
 * ```typescript
 * const { projects, isLoading, error, createProject } = useProjects();
 *
 * // Projects are already loaded from Redux
 * console.log(projects);
 *
 * // Force refresh if needed
 * await fetchProjects(true);
 *
 * // Create a new project
 * await createProject("project-123", {
 *   projectName: "My Project",
 *   projectDescription: "Description",
 *   alias: "user-alias"
 * });
 * ```
 */
export const useProjects = () => {
  const dispatch = useDispatch<AppDispatch>();
  const projectsState = useSelector((state: reducerType) => state.Projects);

  /**
   * Get all projects as an array sorted by most recently updated
   */
  const projects = projectsState.allProjectIds.map(
    (id) => projectsState.projects[id],
  );

  /**
   * Get a single project by ID
   */
  const getProjectById = (projectId: string): Project | undefined => {
    return projectsState.projects[projectId];
  };

  /**
   * Fetch projects from the API and update Redux store.
   * When nextToken is provided, appends the next page to the existing list.
   * Otherwise replaces the list (initial load / refresh).
   * Skips fetching if projects are already loaded unless force is true.
   *
   * @param force - Force fetch even if projects are already loaded (ignored when nextToken is provided)
   * @param nextToken - Pagination token to fetch the next page of results
   */
  const fetchProjects = async (force = false, nextToken?: string) => {
    // Skip if already loaded, not forcing, and not paginating
    if (projectsState.hasLoaded && !force && !nextToken) {
      return;
    }

    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await getProjectsAPI({
        nextToken: nextToken ? nextToken : undefined,
        pageSize: DEFAULT_PROJECT_MAX_PAGE_SIZE,
      });

      if (response.success && response.data.projects) {
        if (nextToken) {
          dispatch(appendProjects(response.data.projects));
        } else {
          dispatch(setProjects(response.data.projects));
        }
        dispatch(setNextToken(response.data.nextToken ?? null));
      } else {
        dispatch(setError("Failed to fetch projects"));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch projects";
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  /**
   * Fetch a single project by ID from the API and update Redux store
   */
  const fetchProject = async (projectId: string) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await getProjectAPI(projectId);

      if (response.success && response.data) {
        dispatch(setProject(response.data));
      } else {
        dispatch(setError("Failed to fetch project"));
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch project";
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  /**
   * Create a new project via API and add to Redux store
   */
  const createProject = async (
    projectId: string,
    projectData: CreateProjectRequest,
  ) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await createProjectAPI(projectId, projectData);

      if (response.success && response.data.project) {
        dispatch(addProject(response.data.project));
        return response.data.project;
      } else {
        dispatch(setError("Failed to create project"));
        throw new Error("Failed to create project");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create project";
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  /**
   * Update an existing project via API and update Redux store
   */
  const updateProject = async (
    projectId: string,
    projectData: UpdateProjectRequest,
  ) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await updateProjectAPI(projectId, projectData);

      if (response.success && response.data.project) {
        dispatch(setProject(response.data.project));
        return response.data.project;
      } else {
        dispatch(setError("Failed to update project"));
        throw new Error("Failed to update project");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to update project";
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  /**
   * Delete a project via API and remove from Redux store
   */
  const deleteProject = async (projectId: string) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const response = await deleteProjectAPI(projectId);

      if (response.success) {
        dispatch(removeProject(projectId));
      } else {
        dispatch(setError("Failed to delete project"));
        throw new Error("Failed to delete project");
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete project";
      dispatch(setError(errorMessage));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

  /**
   * Clear all projects from Redux store (useful for logout)
   */
  const clearAllProjects = () => {
    dispatch(clearProjects());
  };

  /**
   * Get a cached asset blob URL for a project by asset type
   */
  const getProjectAsset = (
    projectId: string,
    assetType: TacoAssetType,
  ): string | undefined => {
    return projectsState.projectAssets[projectId]?.[assetType];
  };

  /**
   * Fetch any project asset from S3 and store as blob URL in Redux.
   * Routes to the same download API but stores results keyed by assetType,
   * so each type is cached independently per project.
   *
   * Returns an object with `blobUrl` (for binary assets like images) and
   * optionally `text` (for text-based assets like markdown summaries).
   * Text is read directly from the in-memory Blob so callers can use it
   * immediately without a separate network request.
   *
   * @param params - Download URL parameters (projectId, versionNumber, assetType).
   *   For text-based asset types (summary, implementation, thinking), the blob
   *   content is automatically read as UTF-8 text and included in the result.
   */
  const fetchProjectAsset = async (
    params: AssetDownloadUrlParams,
  ): Promise<{ blobUrl: string; text?: string } | null> => {
    try {
      // Get presigned URL from API
      const response = await getAssetDownloadUrl(params);
      if (!response.success || !response.data.presignedUrl) {
        throw new Error("Failed to get presigned URL");
      }

      // Fetch the asset from S3
      const assetResponse = await fetch(response.data.presignedUrl);
      if (!assetResponse.ok) {
        throw new Error("Failed to fetch asset from S3");
      }

      // Convert to blob and create blob URL
      const blob = await assetResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      // For text-based assets, read the text directly from the in-memory
      // Blob using the Blob API. This avoids an extra network round-trip
      // and works regardless of Content Security Policy restrictions.
      let text: string | undefined;
      if (TEXT_ASSET_TYPES.has(params.assetType)) {
        try {
          text = await blob.text();
        } catch (textError) {
          console.error(
            `Failed to read ${params.assetType} blob as text:`,
            textError,
          );
        }
      }

      // Store in Redux keyed by projectId + assetType
      dispatch(
        setProjectAsset({
          projectId: params.projectId,
          assetType: params.assetType,
          blobUrl,
        }),
      );

      return { blobUrl, text };
    } catch (error) {
      console.error(`Error fetching ${params.assetType} asset:`, error);
      throw error;
    }
  };

  /**
   * Clear a specific asset blob URL for a project and revoke the blob
   */
  const clearProjectAssetUrl = (
    projectId: string,
    assetType: TacoAssetType,
  ) => {
    const blobUrl = projectsState.projectAssets[projectId]?.[assetType];
    if (blobUrl && blobUrl.startsWith("blob:")) {
      URL.revokeObjectURL(blobUrl);
    }
    dispatch(clearProjectAsset({ projectId, assetType }));
  };

  /**
   * Clear all cached asset blob URLs for a project and revoke them
   */
  const clearAllProjectAssetUrls = (projectId: string) => {
    const assets = projectsState.projectAssets[projectId];
    if (assets) {
      Object.values(assets).forEach((blobUrl) => {
        if (blobUrl && blobUrl.startsWith("blob:")) {
          URL.revokeObjectURL(blobUrl);
        }
      });
    }
    dispatch(clearAllProjectAssets(projectId));
  };

  /**
   * Whether every project has been loaded into memory (no more pages to fetch).
   */
  const allProjectsLoaded =
    projectsState.hasLoaded && projectsState.nextToken === null;

  /**
   * Search projects via the server-side API.
   * Returns the matching projects directly (does NOT mutate the Redux store
   * so the full project list stays intact).
   */
  const searchProjects = async (
    search: string,
    nextToken?: string,
  ): Promise<{ projects: Project[]; nextToken?: string }> => {
    const response = await getProjectsAPI({
      search,
      pageSize: DEFAULT_PROJECT_MAX_PAGE_SIZE,
      nextToken,
    });

    if (response.success && response.data.projects) {
      return {
        projects: response.data.projects,
        nextToken: response.data.nextToken,
      };
    }
    throw new Error("Failed to search projects");
  };

  return {
    // State
    projects,
    isLoading: projectsState.isLoading,
    error: projectsState.error,
    hasLoaded: projectsState.hasLoaded,
    nextToken: projectsState.nextToken,
    allProjectsLoaded,

    // Getters
    getProjectById,
    getProjectAsset,

    // Actions
    fetchProjects,
    fetchProject,
    searchProjects,
    createProject,
    updateProject,
    deleteProject,
    clearAllProjects,
    fetchProjectAsset,
    clearProjectAssetUrl,
    clearAllProjectAssetUrls,
  };
};
